import z from "zod";
import { ChatMessageRole } from "./enums/ChatMessageRole";
import { HelpAudience } from "./enums/HelpAudience";
import { HelpLanguage } from "./enums/HelpLanguage";
import { StoreIdScheme } from "./ids";

export const HelpPathScheme = z
  .string()
  .regex(/^\/([a-z0-9.-]+(\/[a-z0-9.-]+)*)?$/);

export const HelpSourceScheme = z.object({
  path: HelpPathScheme,
  title: z.string(),
  heading: z.string(),
  anchor: z.string(),
});

export const HelpSearchResultScheme = HelpSourceScheme.extend({
  section: z.string(),
  excerpt: z.string(),
});

export const RestHelpNavigationScheme = z.object({
  brand: z.string(),
  sections: z.array(
    z.object({
      title: z.string(),
      pages: z.array(z.object({ path: HelpPathScheme, title: z.string() })),
    }),
  ),
});

export const RestHelpPageScheme = z.object({
  path: HelpPathScheme,
  title: z.string(),
  description: z.string().optional(),
  section: z.string(),
  markdown: z.string(),
  lang: z.enum(HelpLanguage).optional(),
});

export const RestHelpSearchScheme = z.object({
  results: z.array(HelpSearchResultScheme),
});

export const RestHelpViewerScheme = z.object({
  authenticated: z.boolean(),
  email: z.string().nullable(),
  brand: z.string(),
  basePath: z.string(),
  audience: z.enum(HelpAudience),
  storeScoped: z.boolean(),
});

export const HelpChatMessageScheme = z.object({
  role: z.enum(ChatMessageRole),
  content: z.string().trim().min(1).max(4000),
});

export const RestHelpChatRequestScheme = z.object({
  messages: z.array(HelpChatMessageScheme).min(1).max(20),
  storeId: StoreIdScheme.optional(),
});

export const HelpChatSourcesEventScheme = z.object({
  type: z.literal("sources"),
  sources: z.array(HelpSourceScheme),
});

export const HelpChatDeltaEventScheme = z.object({
  type: z.literal("delta"),
  text: z.string(),
});

export const HelpChatDoneEventScheme = z.object({
  type: z.literal("done"),
});

export const HelpChatErrorEventScheme = z.object({
  type: z.literal("error"),
  message: z.string(),
});

export const HelpChatStreamEventScheme = z.discriminatedUnion("type", [
  HelpChatSourcesEventScheme,
  HelpChatDeltaEventScheme,
  HelpChatDoneEventScheme,
  HelpChatErrorEventScheme,
]);

export type HelpSource = z.infer<typeof HelpSourceScheme>;
export type HelpSearchResult = z.infer<typeof HelpSearchResultScheme>;
export type RestHelpNavigation = z.infer<typeof RestHelpNavigationScheme>;
export type RestHelpPage = z.infer<typeof RestHelpPageScheme>;
export type RestHelpSearch = z.infer<typeof RestHelpSearchScheme>;
export type RestHelpViewer = z.infer<typeof RestHelpViewerScheme>;
export type HelpChatMessage = z.infer<typeof HelpChatMessageScheme>;
export type RestHelpChatRequest = z.infer<typeof RestHelpChatRequestScheme>;
export type HelpChatStreamEvent = z.infer<typeof HelpChatStreamEventScheme>;
